import React, { useCallback, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { BsChevronDown } from "react-icons/bs";

import { useToast } from "../../common/hooks/useToast";
import { RESET_RATE_LIMIT } from "./rateLimits.mutation";
import { describeError, getRateLimitRejection, pluralize } from "./rateLimit";

/**
 * The old one-input reset, kept but demoted.
 *
 * It clears only keys that literally contain the string typed, with no
 * correlation behind it — which is why it is no longer the primary action: an
 * admin who pastes a phone number here clears that number's own counters and
 * leaves the IP and device that are actually blocking the signup untouched,
 * with a success message either way. `unblockOtpRecipient` resolves the whole
 * member instead.
 *
 * Still the only tool for a key with no OTP send history to resolve through,
 * so deleting it would strand those cases.
 */
export const RawCounterReset: React.FC = (): JSX.Element => {
  const [resetRateLimit] = useMutation(RESET_RATE_LIMIT);
  const toast = useToast();

  const [dimensionValue, setDimensionValue] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetOutcome, setResetOutcome] = useState<{
    target: string;
    cleared: number;
  } | null>(null);

  const handleReset = useCallback(async () => {
    const target = dimensionValue.trim();

    if (!target) return;

    setResetting(true);
    setResetOutcome(null);

    try {
      const result = await resetRateLimit({
        variables: { dimensionValue: target },
      });

      setResetOutcome({
        target,
        cleared: Number(result.data?.resetRateLimit ?? 0),
      });
      setDimensionValue("");
    } catch (mutationError) {
      // The global Apollo error link owns rate-limit notices; toasting here
      // too would double them.
      if (!getRateLimitRejection(mutationError)) {
        toast.error(describeError(mutationError, "Could not clear the counters"));
      }
    } finally {
      setResetting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionValue, resetRateLimit]);

  return (
    <Accordion disableGutters sx={{ mt: 3 }}>
      <AccordionSummary expandIcon={<BsChevronDown />}>
        <Typography variant="subtitle1" fontWeight={600}>
          Advanced — clear a raw counter key
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Clears only the counter keys that <strong>literally contain</strong>{" "}
          the string you type. It does not resolve the value to a person, so it
          will not touch the IP or device behind a phone number — use{" "}
          <strong>Unblock this recipient</strong> above for anyone who went
          through the OTP flow.
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Reach for this only when there is no OTP history to resolve through:
          a <code>verificationSid</code> argument counter, or an IP address with
          no <code>OtpSendLog</code> rows.
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          flexWrap="wrap"
          rowGap={2}
        >
          <TextField
            label="Exact counter value"
            helperText="Matched as a literal substring — phone numbers need the country code"
            size="small"
            value={dimensionValue}
            disabled={resetting}
            onChange={(event) => setDimensionValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleReset();
            }}
            sx={{ minWidth: 320 }}
          />

          <Button
            variant="outlined"
            sx={{ mt: 0.5 }}
            disabled={resetting || !dimensionValue.trim()}
            onClick={handleReset}
          >
            {resetting ? "Clearing…" : "Clear counters"}
          </Button>
        </Stack>

        {resetOutcome && (
          /*
           * Always `success`: the count is keys deleted, not a measure of
           * whether anything was blocked. Zero is normal and harmless, so
           * leading with the number made a fine outcome read as a failure.
           */
          <Alert severity="success" sx={{ mt: 2 }}>
            Rate limit counters cleared for <code>{resetOutcome.target}</code>.
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {resetOutcome.cleared === 0
                ? "Nothing was actively counting against it — the clear still applied."
                : `${pluralize(
                    resetOutcome.cleared,
                    "counter"
                  )} released. One key is held per window per tier, so this is` +
                  " normally several times the number of requests."}
            </Typography>
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
